import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Shield, X, Sun, Moon } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { MegaMenu } from "@/components/MegaMenu";
import { SocialLinks } from "@/components/SocialLinks";
import { SearchBar } from "@/components/SearchBar";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTheme } from "@/context/ThemeContext";

const NavItem = ({ link, className, onClick, testId }) => {
  const cls = `${className} ${link.highlight ? "text-sky-400 hover:text-sky-300" : ""}`;
  const internal = link.url.startsWith("/") && !link.url.startsWith("/#");
  if (internal) return <Link to={link.url} className={cls} onClick={onClick} data-testid={testId}>{link.label}</Link>;
  return <a href={link.url} className={cls} onClick={onClick} data-testid={testId} {...(/^https?:/.test(link.url) ? { target: "_blank", rel: "noreferrer" } : {})}>{link.label}</a>;
};

export const Navbar = () => {
  const [shows, setShows] = useState([]);
  const [menuGroups, setMenuGroups] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { settings: ctxSettings } = useSettings();
  const settings = ctxSettings || {};
  const navLinks = settings.nav_links || [];

  useEffect(() => {
    api.get("/shows").then((r) => setShows(r.data)).catch(() => {});
    api.get("/menu").then((r) => setMenuGroups(r.data)).catch(() => {});
  }, []);

  const navLink = "text-base font-semibold text-slate-300 hover:text-white transition-colors duration-200 whitespace-nowrap";

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-2 sm:gap-6">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group shrink-0 min-w-0" data-testid="nav-logo-link">
          <Logo size={48} className="transition-transform duration-300 group-hover:rotate-6" />
          <span className="wordmark font-display font-black text-[20px] sm:text-[34px] uppercase tracking-tight leading-none text-white whitespace-nowrap" data-testid="nav-wordmark">Underdog Podcasts</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 shrink-0" data-testid="nav-links">
          {navLinks.map((l, i) => <NavItem key={`${l.label}-${i}`} link={l} className={navLink} testId={`nav-link-${i}`} />)}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1">
            <SocialLinks settings={settings} size={16} className="hidden xl:flex" />
            <Link to={user ? "/admin" : "/admin/login"} title={user ? "Dashboard" : "Admin"} aria-label={user ? "Dashboard" : "Admin"} className="p-2 rounded-full text-slate-400 transition-colors duration-200 hover:bg-white/5 hover:text-sky-300" data-testid="nav-admin-link">
              <Shield size={16} />
            </Link>
            <button onClick={toggleTheme} title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"} aria-label="Toggle light/dark mode" className="p-2 rounded-full text-slate-400 transition-colors duration-200 hover:bg-white/5 hover:text-sky-300" data-testid="theme-toggle">
              {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
            </button>
          </div>
          <button className="md:hidden p-2 text-slate-200" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 glass px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="nav-mobile-menu">
          <SearchBar testId="mobile-search-input" />
          <p className="eyebrow">Our Shows</p>
          <div className="flex flex-wrap gap-2 pb-2" data-testid="mobile-category-links">
            {menuGroups.map((g) => <Link key={g.slug} to={`/browse/${g.slug}`} onClick={() => setMobileOpen(false)} className="rounded-full border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200" data-testid={`mobile-category-${g.slug}`}>{g.name}</Link>)}
          </div>
          <div className="grid grid-cols-1 gap-1">
            {shows.map((s) => (
              <Link key={s.id} to={`/shows/${s.slug}`} onClick={() => setMobileOpen(false)} className="text-sm py-1.5 text-slate-200" data-testid={`mobile-show-link-${s.slug}`}>{s.name}</Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 pt-2 border-t border-white/10" data-testid="mobile-nav-links">
            {navLinks.map((l, i) => <NavItem key={`${l.label}-${i}`} link={l} className={navLink} onClick={() => setMobileOpen(false)} testId={`mobile-nav-link-${i}`} />)}
            <Link to="/admin/login" className={navLink} onClick={() => setMobileOpen(false)}>Admin</Link>
            <button onClick={toggleTheme} className={`${navLink} inline-flex items-center gap-1.5`} data-testid="mobile-theme-toggle">{theme === "light" ? <><Moon size={14} /> Dark</> : <><Sun size={14} /> Light</>}</button>
          </div>
          <SocialLinks settings={settings} className="pt-2 border-t border-white/10" />
        </div>
      )}
      <MegaMenu />
    </header>
  );
};
